import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { AnimatePresence } from 'framer-motion';

// Import Admin Components
import { AdminHeader, AdminTabs } from '../components/admin/AdminLayout';
import { LoginForm } from '../components/admin/LoginForm';
import { ProductsTab } from '../components/admin/ProductsTab';
import { SettingsTab } from '../components/admin/SettingsTab';
import { AIToolsTab } from '../components/admin/AIToolsTab';
import { BackupTab } from '../components/admin/BackupTab';
import { ProductModal } from '../components/admin/ProductModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth helper functions
const getToken = () => localStorage.getItem('admin_token');
const setToken = (token) => localStorage.setItem('admin_token', token);
const removeToken = () => localStorage.removeItem('admin_token');

const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };
  return fetch(url, { ...options, headers });
};

export default function Admin() {
  const { isArabic } = useLanguage();
  
  // Auth States
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  // Data States
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ 
    logoUrl: '', 
    whatsappNumber: '0943999339',
    email: '',
    phone: '',
    address: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: ''
  });
  const [loading, setLoading] = useState(true);
  
  // UI States
  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsPerPage] = useState(12);
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  
  // Settings States
  const [savingSettings, setSavingSettings] = useState(false);
  
  // AI Diagnosis States
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagnosisReport, setDiagnosisReport] = useState(null);
  
  // Backup States
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setCheckingAuth(false);
        return;
      }

      try {
        const res = await authFetch(`${API}/auth/verify`);
        if (res.ok) {
          setIsAuthenticated(true);
        } else {
          removeToken();
        }
      } catch (err) {
        removeToken();
      }
      setCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // Fetch data functions
  const fetchProducts = useCallback(async (page = 1) => {
    try {
      const res = await authFetch(`${API}/products?include_hidden=true&page=${page}&limit=${productsPerPage}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setProducts(data);
        setTotalProducts(data.length);
        setTotalPages(1);
      } else {
        setProducts(data.products || []);
        if (data.pagination) {
          setTotalProducts(data.pagination.total);
          setTotalPages(data.pagination.pages);
          setCurrentPage(data.pagination.page);
        }
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, [productsPerPage]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/categories`);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/settings`);
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchProducts(1), fetchCategories(), fetchSettings()]);
        setLoading(false);
      };
      loadData();
    }
  }, [isAuthenticated, fetchProducts, fetchCategories, fetchSettings]);

  // Auth Handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: credentials.username, password: credentials.password })
      });

      if (res.ok) {
        const data = await res.json();
        setToken(data.access_token);
        setIsAuthenticated(true);
      } else {
        setLoginError(isArabic ? 'اسم المستخدم أو كلمة المرور غير صحيحة' : 'Invalid username or password');
      }
    } catch (err) {
      setLoginError(isArabic ? 'حدث خطأ في الاتصال' : 'Connection error');
    }
  };

  const handleLogout = () => {
    removeToken();
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
  };

  // Product Handlers
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleSaveProduct = async (formData) => {
    setSavingProduct(true);
    try {
      const url = editingProduct 
        ? `${API}/products/${editingProduct.id}`
        : `${API}/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        await fetchProducts(currentPage);
        setShowModal(false);
        setEditingProduct(null);
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error saving product:', err);
    }
    setSavingProduct(false);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(isArabic ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      const res = await authFetch(`${API}/products/${productId}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchProducts(currentPage);
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const handleToggleVisibility = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    try {
      const res = await authFetch(`${API}/products/${productId}/visibility?isVisible=${!product.isVisible}`, {
        method: 'PATCH'
      });
      if (res.ok) {
        await fetchProducts(currentPage);
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchProducts(newPage);
    }
  };

  // Settings Handlers
  const handleSaveSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      const res = await authFetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      if (res.ok) {
        setSettings(newSettings);
        alert(isArabic ? 'تم حفظ الإعدادات!' : 'Settings saved!');
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error saving settings:', err);
    }
    setSavingSettings(false);
  };

  // AI Diagnosis Handlers
  const handleRunDiagnosis = async () => {
    setDiagnosisLoading(true);
    setDiagnosisReport({ progress: 0 });
    
    try {
      const res = await authFetch(`${API}/diagnose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoFix: false })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Poll for results
        pollDiagnosisResults(data.reportId);
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error starting diagnosis:', err);
      setDiagnosisLoading(false);
    }
  };

  const pollDiagnosisResults = async (reportId) => {
    const pollInterval = setInterval(async () => {
      try {
        const res = await authFetch(`${API}/diagnose/${reportId}`);
        if (res.ok) {
          const data = await res.json();
          setDiagnosisReport(data);
          
          if (data.status === 'completed' || data.status === 'error') {
            clearInterval(pollInterval);
            setDiagnosisLoading(false);
          }
        }
      } catch (err) {
        clearInterval(pollInterval);
        setDiagnosisLoading(false);
      }
    }, 1000);
  };

  // Backup Handlers
  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await authFetch(`${API}/backup/create`, { method: 'POST' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `albasma_backup_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error downloading backup:', err);
    }
    setBackupLoading(false);
  };

  const handleRestoreBackup = async (file) => {
    if (!window.confirm(isArabic ? 'سيتم استبدال جميع البيانات. هل أنت متأكد؟' : 'All data will be replaced. Are you sure?')) {
      return;
    }
    
    setRestoreLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await authFetch(`${API}/backup/restore`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(isArabic ? `تم استعادة ${data.products_restored} منتج` : `Restored ${data.products_restored} products`);
        await fetchProducts(1);
        await fetchSettings();
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Error restoring backup:', err);
    }
    setRestoreLoading(false);
  };

  // Loading states
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginForm
        isArabic={isArabic}
        credentials={credentials}
        setCredentials={setCredentials}
        onSubmit={handleLogin}
        loginError={loginError}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" dir={isArabic ? 'rtl' : 'ltr'} data-testid="admin-page">
      {/* Header */}
      <AdminHeader isArabic={isArabic} onLogout={handleLogout} />

      {/* Tab Navigation */}
      <AdminTabs 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        isArabic={isArabic} 
      />

      {/* Main Content */}
      <main className="container mx-auto">
        {activeTab === 'products' && (
          <ProductsTab
            isArabic={isArabic}
            products={products}
            categories={categories}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onAddProduct={handleAddProduct}
            onEditProduct={handleEditProduct}
            onDeleteProduct={handleDeleteProduct}
            onToggleVisibility={handleToggleVisibility}
            currentPage={currentPage}
            totalPages={totalPages}
            totalProducts={totalProducts}
            onPageChange={handlePageChange}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            isArabic={isArabic}
            settings={settings}
            onSaveSettings={handleSaveSettings}
            saving={savingSettings}
          />
        )}

        {activeTab === 'ai-tools' && (
          <AIToolsTab
            isArabic={isArabic}
            onRunDiagnosis={handleRunDiagnosis}
            diagnosisReport={diagnosisReport}
            diagnosisLoading={diagnosisLoading}
          />
        )}

        {activeTab === 'backup' && (
          <BackupTab
            isArabic={isArabic}
            onDownloadBackup={handleDownloadBackup}
            onRestoreBackup={handleRestoreBackup}
            backupLoading={backupLoading}
            restoreLoading={restoreLoading}
          />
        )}
      </main>

      {/* Product Modal */}
      <AnimatePresence>
        {showModal && (
          <ProductModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setEditingProduct(null);
            }}
            product={editingProduct}
            categories={categories}
            isArabic={isArabic}
            onSave={handleSaveProduct}
            saving={savingProduct}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
